"use client";

import React, { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SearchCheck, FileText, Share2, Star, Code as Code2, ChartBar as BarChart3, ArrowRight, Check as CheckCircle2, UserCheck, Sparkles } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const processSteps = [{
  number: "01",
  title: "Technical & Strategic SEO Audit",
  tagline: "Uncovering Bottlenecks & Growth Levers",
  description: "I perform a deep, hands-on technical audit of your domain, analyzing crawlability, page speed, indexation, site architecture, and existing keyword footprints to build a clear, prioritized action plan.",
  icon: SearchCheck,
  deliverables: ["Core Web Vitals Analysis", "Competitor Content Gap Analysis", "Custom Technical Roadmap"]
}, {
  number: "02",
  title: "On-Page & Content Optimization",
  tagline: "Aligning Content with Search Intent",
  description: "I systematically optimize your high-priority pages and structure targeted content strategies that satisfy real user intent, refining headings, semantic keywords, meta tags, and internal linking.",
  icon: FileText,
  deliverables: ["Intent-Matched Keywords", "On-Page Content Enhancements", "Internal Link Architecture"]
}, {
  number: "03",
  title: "Authority & Citation Infrastructure",
  tagline: "Establishing Local & Niche Entity Signals",
  description: "I build high-trust, consistent citations and authoritative backlinks tailored to your market. This solidifies your entity presence across Google and industry-specific directories.",
  icon: Share2,
  deliverables: ["Niche & Local Citations", "Entity Trust Building", "Google Business Profile Optimisation"]
}, {
  number: "04",
  title: "Review Strategy & Trust Signals",
  tagline: "Converting Visitors into Qualified Leads",
  description: "Traffic only matters if it converts. I guide you in setting up review acquisition workflows and prominent E-E-A-T trust signals that build immediate credibility with prospective clients.",
  icon: Star,
  deliverables: ["Review Acquisition Workflows", "E-E-A-T Signal Placement", "Conversion Micro-Fixes"]
}, {
  number: "05",
  title: "Structured Data & AI Schema Markup",
  tagline: "Optimizing for Modern & AI Search",
  description: "I implement precise JSON-LD Schema markup (LocalBusiness, Article, FAQ, Service) so search engines and AI assistants understand your brand entities and answer prospective client queries accurately.",
  icon: Code2,
  deliverables: ["Custom JSON-LD Schema", "Entity Relationship Mapping", "AI Search Engine Readiness"]
}, {
  number: "06",
  title: "Transparent Reporting & Continuous Growth",
  tagline: "Honest Metrics & Proactive Iteration",
  description: "You receive direct monthly reporting with plain-English insights on organic rankings, leads, and revenue impact — backed by continuous, proactive strategic refinements.",
  icon: BarChart3,
  deliverables: ["Plain-English Performance Dashboard", "Monthly Strategy Video Calls", "Proactive Strategy Refinements"]
}];
export default function Methodology() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".step-card", {
        y: 35
      });
      gsap.to(".step-card", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        stagger: 0.12,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".step-card", {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: containerRef
  });
  return <section id="methodologystepprocess" data-nav="light" className="bg-background text-foreground py-20 md:py-28 overflow-hidden relative">
      <div aria-hidden="true" className="absolute top-1/4 -left-32 w-96 h-96 bg-secondary/40 rounded-full blur-3xl pointer-events-none" />
      <div aria-hidden="true" className="absolute bottom-10 -right-32 w-96 h-96 bg-accent/10 rounded-full blur-3xl pointer-events-none" />

      <div ref={containerRef} className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        <div className="max-w-3xl mx-auto text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 px-3.5 py-1 text-xs font-semibold uppercase tracking-wider border-primary/20 bg-secondary/50 text-foreground">
            <Sparkles className="w-3.5 h-3.5 text-accent mr-1.5" />
            Clear, Structured & Hands-On
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-default text-foreground mb-6">
            My Step-by-Step SEO Methodology
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed font-default">
            When you work with me, there are no account managers, outsourced freelancers, or junior staff. 
            You get direct consultant access to a tailored 6-step framework designed to deliver sustainable search visibility and qualified leads.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 relative">
          {processSteps.map((step, i) => {
          const IconComponent = step.icon;
          return <div key={step.number} data-index={i} className="step-card h-full" style={{
            opacity: 0
          }}>
                <Card className="h-full border border-border bg-card hover:border-primary/40 hover:shadow-lg transition-all duration-300 rounded-xl flex flex-col justify-between overflow-hidden relative group">
                  
                  <div className="h-1 w-full bg-gradient-to-r from-primary to-accent opacity-80 group-hover:opacity-100 transition-opacity" />
                  
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-3xl font-bold font-serif text-primary tracking-tight">
                        {step.number}
                      </span>
                      <div className="p-2.5 rounded-lg bg-secondary text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                        <IconComponent className="w-5 h-5" />
                      </div>
                    </div>
                    
                    <CardTitle className="text-xl font-semibold text-foreground font-default group-hover:text-primary transition-colors">
                      {step.title}
                    </CardTitle>
                    
                    <CardDescription className="text-xs font-medium text-accent font-default">
                      {step.tagline}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="flex-1 flex flex-col justify-between space-y-6 pt-0">
                    <p className="text-sm text-muted-foreground leading-relaxed font-default">
                      {step.description}
                    </p>

                    <div className="pt-4 border-t border-border/60">
                      <span className="text-xs font-semibold text-foreground tracking-wider uppercase mb-2 block font-default">
                        Key Deliverables:
                      </span>
                      <ul className="space-y-1.5">
                        {step.deliverables.map((item, idx) => <li key={idx} className="text-xs text-muted-foreground flex items-center gap-2 font-default">
                            <CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0" />
                            <span>{item}</span>
                          </li>)}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>

        <div className="mt-16 md:mt-20 p-8 sm:p-10 rounded-2xl bg-secondary/60 border border-border/80 shadow-xs flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex items-start gap-4 max-w-2xl">
            <div className="p-3 bg-primary text-primary-foreground rounded-xl shrink-0 hidden sm:block">
              <UserCheck className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-foreground font-default mb-2">
                Work Directly With Yassine Essaadi
              </h3>
              <p className="text-sm text-muted-foreground font-default leading-relaxed">
                Ready to transform your search engine presence with clear strategy and honest execution? Let us talk through your website goals in a free initial consultation.
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto shrink-0">
            <Button asChild size="lg" className="w-full sm:w-auto font-medium shadow-md">
              <Link to="/ContactPage">
                Book a Free Consultation
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-medium">
              <Link to="/CaseStudiesPage">
                View Case Studies
              </Link>
            </Button>
          </div>
        </div>

      </div>
    </section>;
}